
import { useSelector } from 'react-redux'
import classes from './SetFilterItem.module.css'
import { useMemo } from 'react'
import { selectFiltersBasedOnKey } from '../../../lib/filters'
import { practicePatientStatusesOptions, practicesOptions, preOpTestsOptions, retrievePayerTypes } from '../../../lib/systemData'
import { getFiltersFromLocalStorageAndRemoveSelectedFilter, setFiltersInLocalStorage } from '../../../lib/filters'
import { markCollectionRepDismissed } from '../../../lib/workQ'
import { applyWorkQFilterChangeAndFetch } from '../../../lib/hooks'
import BodySimpleModalV3 from '../../common/modal/BodySimpleModalV3'
import store from '../../../store'

function getCurrentFilters() {
    return store.getState().filter.filters || {}
}

function shouldKeepFilterEntry(entry, itemValue, isAsync) {
    if (isAsync) {
        return entry?.value != itemValue && entry?.id != itemValue
    }
    if (entry && typeof entry === 'object') {
        return entry.value != itemValue && entry.id != itemValue
    }
    return entry != itemValue
}

function buildPayerTypeStatusDisplay(activeValue) {
    const selected = []
    const payerTypes = retrievePayerTypes()

    ;(activeValue || []).forEach((element) => {
        let statuses = []
        if (element && element.payer_status_id && element.payer_status_id.length) {
            const exactGroup = payerTypes.find((pt) => pt.value == element.payer_type_id)

            if (exactGroup && exactGroup.payer_statuses) {
                const matchedStatuses = exactGroup.payer_statuses.filter((elem) =>
                    element.payer_status_id.includes(elem.id),
                )
                statuses = matchedStatuses.map((sel) => ({
                    value: sel.id,
                    label: sel.name,
                }))
            }
        }

        let stringKey = 'status_'
        if (statuses && statuses.length) {
            statuses.forEach((status) => {
                stringKey = stringKey + status.label + status.value
            })
        }

        const foundPayerType = payerTypes.find((pt) => pt.value == element?.payer_type_id)
        if (foundPayerType) {
            selected.push({
                payer_type: foundPayerType,
                payer_status: statuses,
                id: `${foundPayerType.value}${stringKey}`,
            })
        }
    })

    return selected.length ? selected : null
}

function buildPracticePatientStatusDisplay(activeValue) {
    const selected = []
    const practices = practicesOptions() || []
    const statuses = practicePatientStatusesOptions(true) || []

    ;(activeValue || []).forEach((element) => {
        let selectedStatuses = []
        if (element && element.practice_patient_status_id && element.practice_patient_status_id.length) {
            selectedStatuses = statuses
                .filter((elem) => element.practice_patient_status_id.includes(elem.value))
                .map((sel) => ({
                    value: sel.value,
                    label: sel.label,
                }))
        }

        let stringKey = 'status_'
        if (selectedStatuses && selectedStatuses.length) {
            selectedStatuses.forEach((status) => {
                stringKey = stringKey + status.label + status.value
            })
        }

        const foundPractice = practices.find((practice) => practice.value == element?.practice_id)
        if (foundPractice) {
            selected.push({
                practice: foundPractice,
                statuses: selectedStatuses,
                id: `${foundPractice.value}${stringKey}`,
            })
        }
    })

    return selected.length ? selected : null
}

function deriveSelectedValues(itemKey, activeValue, filtersValues) {
    const filterDef = selectFiltersBasedOnKey(itemKey)
    if (!filterDef) {
        return null
    }

    if (filterDef.type === 'multiple_select') {
        if (itemKey != 'payer_status') {
            const selected = filterDef.options?.filter((elem) => activeValue?.includes(elem.value)) || []
            return selected.length ? selected : null
        }

        const exactGroup = filterDef.options?.find((opt) => opt.value == filtersValues?.payer_type)
        if (exactGroup?.payer_statuses) {
            const selected = exactGroup.payer_statuses.filter((elem) => activeValue?.includes(elem.id))
            const mapArr = selected.map((sel) => ({
                value: sel.id,
                label: sel.name,
            }))
            return mapArr.length ? mapArr : null
        }
        return null
    }

    if (filterDef.type === 'group_select_multiple' && itemKey === 'location') {
        const practices = practicesOptions()
        const locations = []
        ;(activeValue || []).forEach((element) => {
            if (practices) {
                practices.forEach((practice) => {
                    if (practice?.locations) {
                        practice.locations.forEach((loc) => {
                            if (loc.id == element) {
                                locations.push(loc)
                            }
                        })
                    }
                })
            }
        })

        if (locations.length) {
            return {
                isLocations: true,
                locations: locations.map((loc) => ({
                    label: loc.code,
                    value: loc.id,
                })),
            }
        }
        return null
    }

    if (filterDef.type === 'checkbox_multi_array') {
        const mapArr = (activeValue || []).map((sel) => ({
            value: sel,
            label: preOpTestsOptions().find((opt) => opt.value == sel)?.label,
        }))
        return mapArr.length ? mapArr : null
    }

    return null
}

function computeNextFiltersAfterPayerTypeStatusRemoval(filtersValues, itemValue, payer_type, type_status) {
    const nextFilters = JSON.parse(JSON.stringify(filtersValues || {}))

    if (type_status === 'type') {
        if (nextFilters.payer_type_filters) {
            const itemIndex = nextFilters.payer_type_filters.findIndex(
                (object) => object.payer_type_id == itemValue,
            )

            if (itemIndex !== -1) {
                if (nextFilters.payer_type_filters.length === 1) {
                    nextFilters.payer_type_filters = null
                } else {
                    nextFilters.payer_type_filters.splice(itemIndex, 1)
                }
            }
        }
    }

    if (type_status === 'status') {
        if (nextFilters.payer_type_filters) {
            const itemIndex = nextFilters.payer_type_filters.findIndex(
                (object) => object.payer_type_id == payer_type,
            )

            if (itemIndex !== -1) {
                const payerTypeFilter = nextFilters.payer_type_filters[itemIndex]
                if (payerTypeFilter?.payer_status_id) {
                    const statusIndex = payerTypeFilter.payer_status_id.findIndex(
                        (object) => object == itemValue,
                    )

                    if (statusIndex !== -1) {
                        if (payerTypeFilter.payer_status_id.length === 1) {
                            payerTypeFilter.payer_status_id = null
                        } else {
                            payerTypeFilter.payer_status_id.splice(statusIndex, 1)
                        }
                    }
                }
            }
        }
    }

    return nextFilters
}



export default function SetFilterItemMulti({item, filtersValues, workQ}) {
    const filters = useSelector(state => state.filter.filters)


    function removeSpecificFilter(itemValue, isAsync) {
        if (item.key == 'attorney_collection_rep' && workQ?.slug) {
            markCollectionRepDismissed(workQ.slug)
        }

        const nextFilters = JSON.parse(JSON.stringify(getCurrentFilters()))
        const current = nextFilters[item.key]

        if (Array.isArray(current)) {
            const remaining = current.filter((entry) => shouldKeepFilterEntry(entry, itemValue, isAsync))

            if (remaining.length === current.length) {
                return
            }

            if (remaining.length) {
                nextFilters[item.key] = remaining
            } else {
                delete nextFilters[item.key]
            }
        } else {
            delete nextFilters[item.key]
        }

        getFiltersFromLocalStorageAndRemoveSelectedFilter(workQ?.slug, item.key, itemValue)
        if (workQ?.slug) {
            setFiltersInLocalStorage(nextFilters, workQ.slug)
            applyWorkQFilterChangeAndFetch(workQ.slug, nextFilters)
        }
    }


    function removeFromPayerTypeStatusFilter(itemValue, payer_type, type_status) {
        const nextFilters = computeNextFiltersAfterPayerTypeStatusRemoval(
            getCurrentFilters(),
            itemValue,
            payer_type,
            type_status,
        )

        if (workQ?.slug) {
            applyWorkQFilterChangeAndFetch(workQ.slug, nextFilters)
        }
    }

    function removePracticePatientStatusFilter() {
        const nextFilters = JSON.parse(JSON.stringify(getCurrentFilters()))
        delete nextFilters.practice_patient_status_filters
        delete nextFilters.practice_patient_status_filters_match

        if (workQ?.slug) {
            setFiltersInLocalStorage(nextFilters, workQ.slug)
            applyWorkQFilterChangeAndFetch(workQ.slug, nextFilters)
        }
    }

    const activeValue = filters?.[item.key]

    const selectedValues = useMemo(
        () => deriveSelectedValues(item.key, activeValue, filtersValues),
        [item.key, activeValue, filtersValues],
    )

    const asyncSelectedValues = useMemo(() => {
        const filterDef = selectFiltersBasedOnKey(item.key)
        if (filterDef?.type === 'select_async') {
            return activeValue?.length ? activeValue : null
        }
        return null
    }, [item.key, activeValue])

    const payerTypeStatus = useMemo(() => {
        if (item.key === 'payer_type_filters') {
            return buildPayerTypeStatusDisplay(activeValue)
        }
        return null
    }, [item.key, activeValue])

    const practicePatientStatus = useMemo(() => {
        if (item.key === 'practice_patient_status_filters') {
            return buildPracticePatientStatusDisplay(activeValue)
        }
        return null
    }, [item.key, activeValue])


    const filterLabel = workQ?.filter_groups?.find((f) => f.key === item.key)?.label
        ?? item.key?.split('_').join(" ")

    return <>
        {selectedValues && !selectedValues.isLocations && <div className={classes.individual_filter_value}>
            <span className={classes.label}>{filterLabel}</span>
            {selectedValues && <div className={classes.group}>
                {selectedValues.map(val => (
                    <div key={val.label} className={classes.individual_filter_value_inner}>
                        {val.label}
                        <button title="remove filter" onClick={removeSpecificFilter.bind(this, val.value, false)} >
                            <i className="ti ti-x"></i>                    
                        </button>
                    </div>
                ))}
            </div>}
        </div>}
        {selectedValues && selectedValues.isLocations && 
            <div className={classes.individual_filter_value}>
                <BodySimpleModalV3 bottomDropDirection={true} message={<div>
                    <div className={classes.group}>
                    {selectedValues.locations.map(val => (
                        <div key={val.label} className={classes.individual_filter_value_inner}>
                            {val.label}
                            <button title="remove filter" onClick={removeSpecificFilter.bind(this, val.value, false)} className={classes.remove_button}>
                                <i className="ti ti-x"></i>                    
                            </button>
                        </div>
                    ))}
                    </div>
                </div>}>
                    <span className={classes.label}>{filterLabel}</span>
                    <div className={classes.group}>
                        <div className={classes.individual_filter_value_inner}>
                            {selectedValues.locations.length} locations
                        </div>
                    </div>
                </BodySimpleModalV3>
                
            </div>
        }

        {/* old code for multiple select, now split betwen normal and locations */}
        {/* {selectedValues && <div className={classes.individual_filter_value}>
            <span className={classes.label}>{item.key?.split('_').join(" ")}</span>
            {selectedValues && <div className={classes.group}>
                {selectedValues.map(val => (
                    <div key={val.label} className={classes.individual_filter_value_inner}>
                        {val.label}
                        <button title="remove filter" onClick={removeSpecificFilter.bind(this, val.value, false)}>
                            <i className="ti ti-x"></i>                    
                        </button>
                    </div>
                ))}
            </div>}
        </div>} */}

        {asyncSelectedValues?.length ? <div className={classes.individual_filter_value}>
            <span className={classes.label}>{filterLabel}</span>
            <div className={classes.group}>
                {asyncSelectedValues.map(val => (
                    <div key={val.value} className={classes.individual_filter_value_inner}>
                        {val.label}
                        <button title="remove filter" onClick={removeSpecificFilter.bind(this, val.value, 'isAsync')}>
                            <i className="ti ti-x"></i>                    
                        </button>
                    </div>
                ))}
            </div>
        </div> : null}

        {payerTypeStatus && <div className={classes.individual_filter_value}>
            <span className={classes.label}>{filterLabel}</span>
            {payerTypeStatus && <div className={classes.payer_type_status_container}>

                {payerTypeStatus.map(val => (
                    <div key={val.id} className={classes.payer_type_status}>
                        <div className={`${classes.individual_filter_value_inner}`}>
                            {val.payer_type?.label}
                            <button title="remove filter" onClick={removeFromPayerTypeStatusFilter.bind(this, val?.payer_type?.value, null, 'type')}>
                                <i className="ti ti-x"></i>                    
                            </button>
                        </div>
                        {val.payer_status && <div className={`${classes.payer_status}`}>
                            {val.payer_status.map(status => (
                                <div key={status.value} className={`${classes.individual_filter_value_inner}`}>
                                    {status.label}
                                    <button title="remove filter" onClick={removeFromPayerTypeStatusFilter.bind(this, status.value, val?.payer_type?.value, 'status')}>
                                        <i className="ti ti-x"></i>                    
                                    </button>
                                </div>
                            ))}
                        </div>}
                    </div>
                ))}
            </div>} 
        </div>}            

        {practicePatientStatus && <div className={classes.individual_filter_value}>
            <div className={`${classes.individual_filter_value_inner}`}>
                <span className={classes.label}>{filterLabel} ({filtersValues?.practice_patient_status_filters_match || 'and'})</span>
                <button title="remove filter" onClick={removePracticePatientStatusFilter}>
                    <i className="ti ti-x"></i>
                </button>
            </div>
            {practicePatientStatus && <div className={classes.payer_type_status_container}>
                {practicePatientStatus.map(val => (
                    <div key={val.id} className={classes.payer_type_status}>
                        <div className={`${classes.individual_filter_value_inner}`}>
                            {val.practice?.label}
                        </div>
                        {val.statuses && <div className={`${classes.payer_status}`}>
                            {val.statuses.map(status => (
                                <div key={status.value} className={`${classes.individual_filter_value_inner}`}>
                                    {status.label}
                                </div>
                            ))}
                        </div>}
                    </div>
                ))}
            </div>}
        </div>}

    </>
}